import { Check, ChevronsUpDown } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppContext } from '@/app/providers/AppContextProvider';
import Modal from '@/components/shared/Modal';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList
} from '@/components/ui/command';
import { Field, FieldDescription, FieldError, FieldLabel } from '@/components/ui/field';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { sectorCodes } from '@/data/sectorCodes';
import { cn } from '@/lib/utils';
import apiService from '@/services/apiService';

// interface FormData {
// 	sector_code: string;
// }

interface Errors {
	sector_code?: string;
}

interface SubmitStatus {
	type: string;
	message: string;
}

interface ModalSectorCodeProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onAfterSubmit?: () => void;
}

const ModalSectorCode = ({ open, onOpenChange, onAfterSubmit }: ModalSectorCodeProps) => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const { appData } = useAppContext();
	const [popoverOpen, setPopoverOpen] = useState(false);
	const [selectedSectorValue, setSelectedSectorValue] = useState('');
	const [errors, setErrors] = useState<Errors>({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitStatus, setSubmitStatus] = useState<SubmitStatus>({ type: '', message: '' });

	// The modal serves two flows that share one component:
	// - Mandatory entry: opened automatically when the backend couldn't detect the
	//   sector code (status PENDING_CUSTOMER_SECTOR_CODE_ENTRY). The user must pick one,
	//   so the modal stays locked (no close/cancel).
	// - On-demand edit: opened from the sidebar to correct an already-detected sector
	//   code. The modal is dismissable and offers a cancel button.
	const isMandatory = appData?.status === 'PENDING_CUSTOMER_SECTOR_CODE_ENTRY';
	const currentSectorName = (appData?.company_info as { sector_name?: string } | undefined)
		?.sector_name;

	// Reset the selection each time the modal opens so a previous pick doesn't linger.
	useEffect(() => {
		if (!open) return;
		setSelectedSectorValue('');
		setErrors({});
		setSubmitStatus({ type: '', message: '' });
	}, [open]);

	const selectedSector = sectorCodes.find((sector) => sector.value === selectedSectorValue);

	const validateForm = (): boolean => {
		const newErrors: Errors = {};
		if (!selectedSectorValue) {
			newErrors.sector_code = t('modals.sectorCode.selectError');
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSelectChange = (value: string) => {
		setSelectedSectorValue(value);
		setPopoverOpen(false);
		// Clear error when user makes a selection
		if (errors.sector_code) {
			setErrors((prev) => ({ ...prev, sector_code: '' }));
		}
	};

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		if (!id) {
			setErrors({ sector_code: t('modals.sectorCode.noIdError') });
			return;
		}

		if (!selectedSectorValue) {
			setErrors({ sector_code: t('modals.sectorCode.selectError') });
			return;
		}

		setIsSubmitting(true);
		setSubmitStatus({ type: '', message: '' });

		try {
			// Parse the sector code value (can be decimal like "17.1")
			// API expects only the main category number (1-70), so extract integer part
			// e.g., "17.1" → 17, "52.3" → 52, "17" → 17
			const sectorCodeValue = Number.parseInt(selectedSectorValue.split('.')[0], 10);

			const payload = {
				sector_code: sectorCodeValue
			};

			await apiService.sectorCode.update(id, payload);
			onAfterSubmit?.();
			onOpenChange(false);
		} catch (error) {
			setSubmitStatus({
				type: 'error',
				message: t('common.error')
			});
			console.warn(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Modal
			id="modalSectorCode"
			open={open}
			onOpenChange={onOpenChange}
			size="md"
			backdrop={isMandatory ? 'static' : true}
			closeButton={!isMandatory}
			keyboard={!isMandatory}
		>
			<div className="py-6 px-4">
				<div className="text-2xl font-bold mb-3">
					{isMandatory ? t('modals.sectorCode.title') : t('modals.sectorCode.titleEdit')}
				</div>
				<p className="mb-6 text-muted-foreground leading-relaxed">
					{isMandatory ? (
						<>
							<span className="sm:inline-block mr-1">{t('modals.sectorCode.subtitle')}</span>
							<span className="sm:inline-block">{t('modals.sectorCode.introduction')}</span>
						</>
					) : (
						<>
							{currentSectorName && (
								<span className="sm:inline-block mr-1">
									{t('modals.sectorCode.currentSector', { sector: currentSectorName })}
								</span>
							)}
							<span className="sm:inline-block">{t('modals.sectorCode.introductionEdit')}</span>
						</>
					)}
				</p>

				{submitStatus.message && (
					<Alert
						variant={submitStatus.type === 'success' ? 'success' : 'destructive'}
						className="mb-6"
					>
						<AlertDescription>{submitStatus.message}</AlertDescription>
					</Alert>
				)}

				<form onSubmit={handleSubmit} noValidate className="space-y-4 text-left">
					<Field data-invalid={!!errors.sector_code}>
						<FieldLabel htmlFor="sector_code">{t('modals.sectorCode.selectLabel')}</FieldLabel>
						<Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
							<PopoverTrigger asChild>
								<Button
									variant="outline"
									role="combobox"
									aria-expanded={popoverOpen}
									className={cn(
										'w-full justify-between pr-3 text-sm',
										!selectedSectorValue && 'text-muted-foreground',
										errors.sector_code && 'border-destructive'
									)}
									disabled={isSubmitting}
									data-testid="modal-sector-code-select"
									iconRight={<ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />}
								>
									<span className="truncate flex-1 min-w-0 text-left">
										{selectedSector
											? selectedSector.label
											: t('modals.sectorCode.selectPlaceholder')}
									</span>
								</Button>
							</PopoverTrigger>
							<PopoverContent
								className="w-full p-0 border-border z-60"
								style={{ width: 'var(--radix-popover-trigger-width)' }}
							>
								<Command>
									<CommandInput placeholder={t('modals.sectorCode.selectPlaceholder')} />
									<CommandList>
										<CommandEmpty>{t('modals.sectorCode.noResults')}</CommandEmpty>
										<CommandGroup>
											{sectorCodes.map((sector) => (
												<CommandItem
													key={sector.value}
													value={sector.label}
													onSelect={() => handleSelectChange(sector.value)}
												>
													<Check
														className={cn(
															'mr-2 h-4 w-4',
															selectedSectorValue === sector.value ? 'opacity-100' : 'opacity-0'
														)}
													/>
													{sector.label}
												</CommandItem>
											))}
										</CommandGroup>
									</CommandList>
								</Command>
							</PopoverContent>
						</Popover>
						<FieldDescription>{t('modals.sectorCode.selectHelp')}</FieldDescription>
						{errors.sector_code && <FieldError>{errors.sector_code}</FieldError>}
					</Field>

					<Button
						type="submit"
						className="w-full mt-6"
						size="lg"
						loading={isSubmitting}
						disabled={isSubmitting}
						data-testid="modal-sector-code-submit-button"
					>
						{t('modals.sectorCode.submitButton')}
					</Button>
					{!isMandatory && (
						<Button
							type="button"
							variant="ghost"
							className="w-full"
							disabled={isSubmitting}
							onClick={() => onOpenChange(false)}
							data-testid="modal-sector-code-cancel-button"
						>
							{t('common.cancel')}
						</Button>
					)}
				</form>
			</div>
		</Modal>
	);
};

export default ModalSectorCode;
